/**
 * Share-ready recordings — the browser implementation of `PlayRecorder`.
 *
 * Two steps (see `recording-plan.ts` for why):
 *
 *  1. Recording: the board hands over what it shows on every animation frame
 *     (`logFrame`), and the microphone is tapped through an AudioWorklet into
 *     raw samples. Nothing is encoded yet.
 *  2. Preparing (`stop`): the log is replayed at exactly 60 fps into a
 *     1920×1080 canvas through the caller's `draw`, encoded as H.264 (WebCodecs)
 *     with AAC audio — native where the browser has it, otherwise FFmpeg's
 *     encoder compiled to WebAssembly (`@mediabunny/aac-encoder`, LGPL; see
 *     THIRD_PARTY_NOTICES.md) — and written as a fast-start MP4 by Mediabunny.
 *
 * Mediabunny and the AAC encoder are imported on demand, so the board does not
 * download them until a recording is prepared.
 */

import { alignAudio, frameAt, OUTPUT, pickAvc, PrepareStalled, slotCount, withStallGuard, type LoggedFrame } from './recording-plan.ts';
import type { PlayRecorder, RecordingResult, StartPlayRecordingOptions } from './types.ts';

/** Taps the microphone into raw per-channel samples, timed against `performance.now()`. */
interface AudioTap {
  /** `performance.now()` at which the first captured sample was heard. */
  readonly firstSampleAt: () => number | undefined;
  stop(): { channels: Float32Array[]; sampleRate: number };
}

const TAP_PROCESSOR = `registerProcessor('gameboard-tap', class extends AudioWorkletProcessor {
  process(inputs) {
    const input = inputs[0];
    if (input && input.length) this.port.postMessage({ time: currentTime, channels: input.map((c) => c.slice(0)) });
    return true;
  }
});`;

async function tapMicrophone(track: MediaStreamTrack): Promise<AudioTap> {
  const context = new AudioContext();
  const moduleUrl = URL.createObjectURL(new Blob([TAP_PROCESSOR], { type: 'text/javascript' }));
  await context.audioWorklet.addModule(moduleUrl);
  URL.revokeObjectURL(moduleUrl);

  const tap = new AudioWorkletNode(context, 'gameboard-tap');
  const chunks: Float32Array[][] = [];
  let firstTime: number | undefined;
  tap.port.onmessage = (event: MessageEvent<{ time: number; channels: Float32Array[] }>) => {
    firstTime ??= event.data.time;
    chunks.push(event.data.channels);
  };
  const source = context.createMediaStreamSource(new MediaStream([track]));
  // The tap must be pulled to run, so it feeds a muted path to the speakers.
  const muted = context.createGain();
  muted.gain.value = 0;
  source.connect(tap).connect(muted).connect(context.destination);
  await context.resume();
  // One anchor between the audio clock and the page's, taken once running.
  const anchorPage = performance.now();
  const anchorAudio = context.currentTime;

  return {
    firstSampleAt: () => (firstTime === undefined ? undefined : anchorPage + (firstTime - anchorAudio) * 1000),
    stop: () => {
      source.disconnect();
      tap.port.onmessage = null;
      void context.close();
      const channelCount = Math.max(1, ...chunks.map((c) => c.length));
      const length = chunks.reduce((sum, c) => sum + (c[0]?.length ?? 0), 0);
      const channels = Array.from({ length: channelCount }, (_, ch) => {
        const out = new Float32Array(length);
        let at = 0;
        for (const chunk of chunks) {
          const data = chunk[ch] ?? chunk[0]!;
          out.set(data, at);
          at += data.length;
        }
        return out;
      });
      return { channels, sampleRate: context.sampleRate };
    },
  };
}

/** A finished recording, waiting to become a video. */
interface Take<F> {
  readonly log: LoggedFrame<F>[];
  readonly duration: number;
  readonly draw: StartPlayRecordingOptions<F>['draw'];
  readonly audio: { channels: Float32Array[]; sampleRate: number; offset: number } | null;
}

export class WebPlayRecorder<F> implements PlayRecorder<F> {
  #log: LoggedFrame<F>[] = [];
  #startedAt = 0;
  #recording = false;
  #mic: MediaStream | null = null;
  #tap: AudioTap | null = null;
  #draw: StartPlayRecordingOptions<F>['draw'] | null = null;
  #take: Take<F> | null = null;

  async isSupported(): Promise<boolean> {
    if (typeof VideoEncoder === 'undefined' || typeof AudioWorkletNode === 'undefined') return false;
    return (await this.#codec()) !== undefined;
  }

  get recording(): boolean {
    return this.#recording;
  }

  async start(options: StartPlayRecordingOptions<F>): Promise<{ hasAudio: boolean }> {
    if (this.#recording) throw new Error('Already recording');
    this.#draw = options.draw;
    this.#log = [];
    // A new recording replaces one that could not be prepared.
    this.#take = null;
    if (options.withAudio) {
      try {
        this.#mic = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        });
        this.#tap = await tapMicrophone(this.#mic.getAudioTracks()[0]!);
      } catch {
        // Permission denied or no device: record without a voice rather than fail.
        this.#releaseMic();
      }
    }
    this.#startedAt = performance.now();
    this.#recording = true;
    return { hasAudio: this.#tap !== null };
  }

  logFrame(frame: F): void {
    if (!this.#recording) return;
    this.#log.push({ t: (performance.now() - this.#startedAt) / 1000, frame });
  }

  async stop(onProgress: (fraction: number) => void = () => undefined): Promise<RecordingResult> {
    if (!this.#recording) throw new Error('Not recording');
    this.#recording = false;
    const duration = (performance.now() - this.#startedAt) / 1000;
    const tap = this.#tap;
    const firstSampleAt = tap?.firstSampleAt();
    const audio = tap?.stop();
    this.#releaseMic();
    const log = this.#log;
    this.#log = [];
    if (log.length === 0) throw new Error('Nothing was recorded.');
    // Kept until a video is made from it, so preparing can start over — after
    // a stall, or when the coach presses Try again — without re-recording.
    this.#take = {
      log,
      duration,
      draw: this.#draw!,
      audio: audio && audio.channels[0]!.length > 0 ? { ...audio, offset: ((firstSampleAt ?? this.#startedAt) - this.#startedAt) / 1000 } : null,
    };
    return this.retry(onProgress);
  }

  get canRetry(): boolean {
    return this.#take !== null;
  }

  /**
   * Prepares the video from the recording just made: with the browser's own
   * (usually hardware) encoder first, and should that stop answering, once
   * more with its software encoder. If both fail the recording is kept, so
   * calling this again (Try again) needs no new recording.
   */
  async retry(onProgress: (fraction: number) => void = () => undefined): Promise<RecordingResult> {
    const take = this.#take;
    if (!take) throw new Error('There is no recording to prepare.');
    // Loading the encoding code is not encoding: on a slow connection it may
    // take a while, and must not be mistaken for a stall.
    const kit = await this.#kit(take.audio !== null);
    for (const acceleration of ['no-preference', 'prefer-software'] as const) {
      onProgress(0);
      const abandon = new AbortController();
      try {
        const result = await withStallGuard((progressed) =>
          this.#encode(take, kit, acceleration, abandon.signal, (fraction) => {
            progressed();
            onProgress(fraction);
          }),
        );
        this.#take = null;
        return result;
      } catch (error) {
        abandon.abort();
        if (!(error instanceof PrepareStalled)) throw error;
        // Stalled: the next round starts over with the other encoder.
      }
    }
    throw new Error('The video couldn’t be prepared. Try again.');
  }

  /** The codec and the encoding code, loaded once per video. */
  async #kit(withAudio: boolean): Promise<{ codec: string; mediabunny: typeof import('mediabunny') }> {
    const codec = await this.#codec();
    if (!codec) throw new Error('This browser cannot encode H.264 video. Please update it.');
    const mediabunny = await import('mediabunny');
    if (withAudio && !(await mediabunny.canEncodeAudio('aac'))) {
      const { registerAacEncoder } = await import('@mediabunny/aac-encoder');
      registerAacEncoder();
    }
    return { codec, mediabunny };
  }

  /** One attempt at turning the recording into an MP4. Stops as soon as `abandoned` fires. */
  async #encode(
    take: Take<F>,
    { codec, mediabunny }: { codec: string; mediabunny: typeof import('mediabunny') },
    acceleration: 'no-preference' | 'prefer-software',
    abandoned: AbortSignal,
    onProgress: (fraction: number) => void,
  ): Promise<RecordingResult> {
    const { log, duration, draw } = take;
    const audio = take.audio;
    const hasAudio = audio !== null;

    const canvas = document.createElement('canvas');
    canvas.width = OUTPUT.width;
    canvas.height = OUTPUT.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not prepare the video.');

    const target = new mediabunny.BufferTarget();
    const output = new mediabunny.Output({ format: new mediabunny.Mp4OutputFormat({ fastStart: 'in-memory' }), target });
    const video = new mediabunny.CanvasSource(canvas, {
      codec: 'avc',
      fullCodecString: codec,
      bitrate: OUTPUT.videoBitrate,
      keyFrameInterval: OUTPUT.keyFrameSeconds,
      latencyMode: 'quality',
      hardwareAcceleration: acceleration,
    });
    output.addVideoTrack(video, { frameRate: OUTPUT.fps });
    const sound = hasAudio ? new mediabunny.AudioSampleSource({ codec: 'aac', bitrate: OUTPUT.audioBitrate }) : null;
    if (sound) output.addAudioTrack(sound);
    // A stalled attempt is given up: release its encoders at once, rather
    // than leave them holding the hardware the next attempt may need.
    const giveUp = (): void => void output.cancel().catch(() => undefined);
    abandoned.addEventListener('abort', giveUp, { once: true });
    const stillWanted = (): void => {
      if (abandoned.aborted) throw new PrepareStalled();
    };
    await output.start();

    const slots = slotCount(duration, OUTPUT.fps);
    const seconds = slots / OUTPUT.fps;
    const voice = audio ? alignAudio(audio.channels, audio.sampleRate, audio.offset, seconds) : null;

    // Second by second: the second's audio, then its frames, so the file
    // interleaves evenly and nothing piles up in memory.
    for (let second = 0; second * OUTPUT.fps < slots; second += 1) {
      if (sound && voice && audio) {
        const from = second * audio.sampleRate;
        const to = Math.min(voice[0]!.length, from + audio.sampleRate);
        if (to > from) {
          const planar = new Float32Array((to - from) * voice.length);
          voice.forEach((channel, i) => planar.set(channel.subarray(from, to), i * (to - from)));
          const sample = new mediabunny.AudioSample({
            data: planar,
            format: 'f32-planar',
            numberOfChannels: voice.length,
            sampleRate: audio.sampleRate,
            timestamp: second,
          });
          await sound.add(sample);
          sample.close();
          stillWanted();
        }
      }
      const last = Math.min(slots, (second + 1) * OUTPUT.fps);
      for (let slot = second * OUTPUT.fps; slot < last; slot += 1) {
        const at = slot / OUTPUT.fps;
        draw(ctx, OUTPUT.width, OUTPUT.height, frameAt(log, at).frame);
        await video.add(at, 1 / OUTPUT.fps);
        stillWanted();
        onProgress((slot + 1) / slots);
      }
    }
    sound?.close();
    video.close();
    await output.finalize();
    abandoned.removeEventListener('abort', giveUp);

    const blob = new Blob([target.buffer!], { type: 'video/mp4' });
    return { blob, mimeType: 'video/mp4', durationMs: Math.round(seconds * 1000), hasAudio, extension: 'mp4' };
  }

  cancel(): void {
    this.#recording = false;
    this.#take = null;
    this.#tap?.stop();
    this.#releaseMic();
    this.#log = [];
  }

  async #codec(): Promise<string | undefined> {
    return pickAvc(async (codec) => {
      try {
        const { supported } = await VideoEncoder.isConfigSupported({
          codec,
          width: OUTPUT.width,
          height: OUTPUT.height,
          bitrate: OUTPUT.videoBitrate,
          framerate: OUTPUT.fps,
        });
        return supported === true;
      } catch {
        return false;
      }
    });
  }

  #releaseMic(): void {
    this.#tap = null;
    this.#mic?.getTracks().forEach((track) => track.stop());
    this.#mic = null;
  }
}
